import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';
import ApplicationLayoutBodyNavigation from '@rio-cloud/rio-uikit/ApplicationLayoutBodyNavigation';
import SubNavigation from '@rio-cloud/rio-uikit/SubNavigation';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { NavLink, Outlet } from 'react-router-dom';

import { useRfidCards } from '@/api/cards';
import type { RfidCardType } from '@/api/schema';
import { FeatureToggles } from '@/configuration/featureToggle/featureToggles';
import ChargingCardSidebar from '@/features/administration/cards/components/ChargingCardSidebar';
import { useFeatureToggle } from '@/hooks/useFeatureToggle';
import type { NavItemType } from '@/layout/AppLayout';
import PageLayout from '@/layout/PageLayout';
import { includesAny } from '@/lib/utils';
import { ADMINISTRATION_CARDS, ADMINISTRATION_CONTRACT, ADMINISTRATION_HISTORY } from '@/routes/Router';
import { useUserStore } from '@/stores/userStore';

const AdministrationPage = () => {
    const { isEligible, account } = useUserStore();
    const permissions = account?.permissions;
    const accountIsOnboarded = account?.isOnboarded;
    const { value: isCardOrderHistoryEnabled } = useFeatureToggle(FeatureToggles.ENABLE_CARD_ORDER_HISTORY);

    // When the Charging cards tab is enabled, cards and order history move to that tab,
    // so Administration only keeps the Charging contract sub-navigation item.
    const subNavItems: NavItemType[] = isCardOrderHistoryEnabled
        ? [
              {
                  key: 'administration.contract',
                  permissions: ['emsp-contract.read'],
                  route: (
                      <NavLink to={ADMINISTRATION_CONTRACT}>
                          <FormattedMessage id='general.subNavigation.chargingContract' />
                      </NavLink>
                  ),
              },
          ]
        : [
              {
                  key: 'administration.cards',
                  permissions: ['emsp-cards.read'],
                  route: (
                      <NavLink to={ADMINISTRATION_CARDS}>
                          <FormattedMessage id='general.subNavigation.chargingCards' />
                      </NavLink>
                  ),
              },
              {
                  key: 'administration.history',
                  permissions: ['emsp-cards.write'],
                  route: (
                      <NavLink to={ADMINISTRATION_HISTORY}>
                          <FormattedMessage id='general.subNavigation.orderHistory' />
                      </NavLink>
                  ),
              },
              {
                  key: 'administration.contract',
                  permissions: ['emsp-contract.read'],
                  route: (
                      <NavLink to={ADMINISTRATION_CONTRACT}>
                          <FormattedMessage id='general.subNavigation.chargingContract' />
                      </NavLink>
                  ),
              },
          ];

    const allowedSubNavItems =
        isEligible && accountIsOnboarded ? subNavItems.filter(item => includesAny(permissions, item.permissions)) : [];

    const { refetch } = useRfidCards();
    const [selectedCard, setSelectedCard] = useState<RfidCardType | undefined>(undefined);

    const openSidebar = (card: RfidCardType) => {
        setSelectedCard(card);
    };

    const dismissSidebar = async (reloadCards: boolean) => {
        if (reloadCards) {
            await refetch();
        }
        setSelectedCard(undefined);
    };

    return (
        <>
            <ApplicationLayout.Sidebar className='right'>
                <ChargingCardSidebar card={selectedCard} open={!!selectedCard} dismiss={dismissSidebar} />
            </ApplicationLayout.Sidebar>
            <ApplicationLayout.Body
                navigation={
                    isCardOrderHistoryEnabled ? undefined : (
                        <ApplicationLayoutBodyNavigation className='has-offset'>
                            <SubNavigation navItems={allowedSubNavItems} />
                        </ApplicationLayoutBodyNavigation>
                    )
                }
                className='responsive'
            >
                <PageLayout>
                    <Outlet context={{ onOpenSidebar: openSidebar, isSidebarOpen: !!selectedCard }} />
                </PageLayout>
            </ApplicationLayout.Body>
        </>
    );
};

export default AdministrationPage;
